import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

/**
 * Gestión del tema (claro / oscuro / automático).
 *
 * - El modo oscuro es el de la app por defecto.
 * - `preference` guarda la elección del usuario en localStorage:
 *     'dark'   -> siempre oscuro (por defecto, como la app original)
 *     'light'  -> siempre claro
 *     'system' -> sigue al sistema operativo
 * - `theme` es el tema resuelto ('dark' | 'light') y se aplica como
 *   `<html data-theme="...">`, que es lo que leen las variables CSS.
 *
 * No altera ningún comportamiento de la app: solo colores.
 */
const STORAGE_KEY = 'ink-admin-theme';
const PREFERENCES = ['dark', 'light', 'system'];
const ORDER = ['dark', 'light', 'system'];

const ThemeContext = createContext(null);

const systemTheme = () => {
    if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
};

const readPreference = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (PREFERENCES.includes(stored)) return stored;
    } catch (e) {
        /* localStorage no disponible */
    }
    return 'dark';
};

export const ThemeProvider = ({ children }) => {
    const [preference, setPreferenceState] = useState(readPreference);
    const [system, setSystem] = useState(systemTheme);

    const theme = preference === 'system' ? system : preference;

    // Aplica el tema resuelto al documento.
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
    }, [theme]);

    // Sigue los cambios del SO mientras la preferencia es "system".
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mq = window.matchMedia('(prefers-color-scheme: light)');
        const handler = () => setSystem(systemTheme());
        mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
        return () => {
            mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler);
        };
    }, []);

    const setPreference = useCallback((value) => {
        if (!PREFERENCES.includes(value)) return;
        setPreferenceState(value);
        try {
            localStorage.setItem(STORAGE_KEY, value);
        } catch (e) {
            /* localStorage no disponible */
        }
    }, []);

    // Alterna en ciclo: oscuro -> claro -> automático -> oscuro.
    const cycle = useCallback(() => {
        setPreference(ORDER[(ORDER.indexOf(preference) + 1) % ORDER.length]);
    }, [preference, setPreference]);

    const value = { preference, theme, setPreference, cycle };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export default ThemeContext;
